/* ============================================
   RARELANE — Theme JS
   Ported from the static build. All cart operations
   now go through Shopify's AJAX Cart API, so the
   drawer, the count and checkout stay in sync with
   the real cart.
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  initCartDrawer();
  initAccordions();
  initFAQ();
  initMobileMenu();
  initScrollReveal();
  initHeroSlider();
  initProductPage();
  initVideoWidget();
  initSizeGuideModal();
});

/* ============================================
   Toast
   ============================================ */

function showToast(message) {
  document.querySelector('.toast')?.remove();

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none">' +
    '<path d="M13 4L6 11L3 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  toast.appendChild(document.createTextNode(' ' + message));
  document.body.appendChild(toast);

  setTimeout(() => toast.classList.add('visible'), 100);
  setTimeout(() => {
    toast.classList.remove('visible');
    setTimeout(() => toast.remove(), 400);
  }, 3000);
}

/* ============================================
   Cart — Shopify AJAX Cart API
   ============================================ */

function openCart() {
  document.querySelector('.cart-overlay')?.classList.add('open');
  document.querySelector('.cart-drawer')?.classList.add('open');
  document.body.classList.add('no-scroll');
}

function closeCart() {
  document.querySelector('.cart-overlay')?.classList.remove('open');
  document.querySelector('.cart-drawer')?.classList.remove('open');
  document.body.classList.remove('no-scroll');
}

function initCartDrawer() {
  document.querySelector('.cart-trigger')?.addEventListener('click', openCart);
  document.querySelector('.cart-overlay')?.addEventListener('click', closeCart);
  document.addEventListener('click', (e) => {
  if (e.target.closest('.cart-close')) {
    closeCart();
  }
});

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.querySelector('.cart-drawer')?.classList.contains('open')) {
      closeCart();
    }
  });

  wireCartLineControls();
}

// Delegated so the controls keep working after the drawer re-renders.
function wireCartLineControls() {
  const drawer = document.querySelector('.cart-drawer');
  if (!drawer) return;

  drawer.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-line]');
    if (!btn) return;

    const line = btn.dataset.line;
    const qty = btn.dataset.qty;
    if (line == null || qty == null) return;

    changeCartLine(line, parseInt(qty, 10));
  });
}

async function changeCartLine(line, quantity) {
  const drawer = document.querySelector('.cart-drawer');
  drawer?.classList.add('loading');

  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Math.max(0, quantity) })
    });
    if (!res.ok) throw new Error('cart change failed');
    await refreshCartDrawer();
  } catch (err) {
    showToast('Could not update your cart. Please try again.');
  } finally {
    drawer?.classList.remove('loading');
  }
}

// Re-renders the drawer section server-side, so prices, discounts
// and line numbers always match what Shopify actually has.
async function refreshCartDrawer() {
  const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
  const markup = await res.text();

  const parsed = new DOMParser().parseFromString(markup, 'text/html');
  const fresh = parsed.querySelector('.cart-drawer');
  const current = document.querySelector('.cart-drawer');

  if (fresh && current) {
    const wasOpen = current.classList.contains('open');
    const curFooter = current.querySelector('.cart-footer');
    const newFooter = fresh.querySelector('.cart-footer');

    if (curFooter && newFooter) {
      /* Shiprocket Checkout attaches to the checkout button/form. Replacing the
         whole drawer would destroy that node and lose the binding, so update
         everything except the footer's checkout form in place. */
      const curHeader = current.querySelector('.cart-header');
      const newHeader = fresh.querySelector('.cart-header');
      const curBody = current.querySelector('.cart-body');
      const newBody = fresh.querySelector('.cart-body');
      if (curHeader && newHeader) curHeader.innerHTML = newHeader.innerHTML;
      if (curBody && newBody) curBody.innerHTML = newBody.innerHTML;

      const curAmount = curFooter.querySelector('.cart-subtotal-amount');
      const newAmount = newFooter.querySelector('.cart-subtotal-amount');
      if (curAmount && newAmount) curAmount.innerHTML = newAmount.innerHTML;
    } else {
      /* Empty <-> filled transition: footer appears/disappears, replace all. */
      current.innerHTML = fresh.innerHTML;
    }

    if (wasOpen) current.classList.add('open');
  }

  const count = parsed.querySelector('.cart-total-count')?.textContent?.trim();
  if (count != null) updateCartCount(parseInt(count, 10) || 0);
}

function updateCartCount(total) {
  const el = document.querySelector('.cart-count');
  if (!el) return;
  el.textContent = total;
  el.classList.toggle('visible', total > 0);
}

async function addToCart(variantId, quantity) {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] })
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.description || 'Could not add to cart');
  }
  return res.json();
}

/* ============================================
   Header / nav
   ============================================ */

function initMobileMenu() {
  const toggle = document.querySelector('.nav-toggle');
  const menu = document.querySelector('.mobile-menu');

  toggle?.addEventListener('click', () => {
    menu?.classList.toggle('open');
    document.body.classList.toggle('menu-open');
  });

  menu?.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      menu.classList.remove('open');
      document.body.classList.remove('menu-open');
    });
  });
}

/* ============================================
   Accordions & FAQs
   ============================================ */

function initAccordions() {
  document.querySelectorAll('.pdp-accordion-item').forEach((item) => {
    item.querySelector('.accordion-header')?.addEventListener('click', () => {
      item.classList.toggle('open');
    });
  });
}

function initFAQ() {
  const items = document.querySelectorAll('.faq-item');

  const setHeight = (item) => {
    const a = item.querySelector('.faq-a');
    if (a) a.style.maxHeight = item.classList.contains('open') ? a.scrollHeight + 'px' : '';
  };

  items.forEach((item) => {
    item.querySelector('.faq-q')?.addEventListener('click', () => {
      const wasOpen = item.classList.contains('open');
      items.forEach((i) => {
        i.classList.remove('open');
        setHeight(i);
      });
      if (!wasOpen) {
        item.classList.add('open');
        setHeight(item);
      }
    });
  });

  items.forEach(setHeight);
}

/* ============================================
   Scroll reveal
   ============================================ */

function initScrollReveal() {
  const reveals = document.querySelectorAll('.reveal');
  if (!reveals.length) return;

  // IntersectionObserver is what un-hides these, so only opt into the
  // hidden start state if it actually exists.
  if (!('IntersectionObserver' in window)) return;

  document.documentElement.classList.add('js-reveal');

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
  );

  reveals.forEach((el) => observer.observe(el));
}

/* ============================================
   Hero slider
   ============================================ */

function initHeroSlider() {
  const slider = document.querySelector('.hero-slider');
  if (!slider) return;

  const slides = Array.from(slider.querySelectorAll('.hero-slide'));
  if (slides.length < 2) return;

  const dots = Array.from(slider.querySelectorAll('.hero-dot'));
  const autoplay = slider.dataset.autoplay === 'true';
  // data-speed is emitted in milliseconds by the section
  const speed = parseInt(slider.dataset.speed, 10) || 6000;
  let index = 0;
  let timer = null;

  const go = (next) => {
    index = (next + slides.length) % slides.length;
    slides.forEach((s, i) => s.classList.toggle('active', i === index));
    dots.forEach((d, i) => d.classList.toggle('active', i === index));
  };

  const start = () => {
    if (!autoplay) return;
    stop();
    timer = setInterval(() => go(index + 1), speed);
  };
  const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };

  dots.forEach((dot, i) =>
    dot.addEventListener('click', () => {
      go(i);
      start();
    })
  );
  slider.querySelector('[data-hero-prev]')?.addEventListener('click', () => {
    go(index - 1);
    start();
  });
  slider.querySelector('[data-hero-next]')?.addEventListener('click', () => {
    go(index + 1);
    start();
  });

  slider.addEventListener('mouseenter', stop);
  slider.addEventListener('mouseleave', start);

  go(0);
  start();
}

/* ============================================
   Product page
   ============================================ */

function initProductPage() {
  const form = document.getElementById('product-form');
  if (!form) return;

  initGallery();
  initSaveButton();

  const select = form.querySelector('[data-variant-select]');
  const variants = readVariantJson(form);
  const selected = {};

  // Pre-select whatever Shopify marked active in the markup.
  form.querySelectorAll('.size-selector button.active').forEach((btn) => {
    selected[btn.dataset.optionIndex] = btn.dataset.size;
  });

  form.querySelectorAll('.size-selector button:not([disabled])').forEach((btn) => {
    btn.addEventListener('click', () => {
      const group = btn.closest('.size-selector');
      group?.querySelectorAll('button').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');

      selected[btn.dataset.optionIndex] = btn.dataset.size;
      btn.closest('.size-selector-wrapper')?.querySelector('.size-error')?.classList.remove('visible');

      applyVariant(matchVariant(variants, selected, form), select);
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const wrappers = form.querySelectorAll('.size-selector-wrapper');
    let missing = false;
    wrappers.forEach((w) => {
      const idx = w.dataset.optionIndex;
      if (!selected[idx]) {
        w.querySelector('.size-error')?.classList.add('visible');
        missing = true;
      }
    });

    if (missing) {
      showToast('Please select a size first');
      return;
    }

    const variantId = select?.value;
    if (!variantId) {
      showToast('That combination is unavailable');
      return;
    }

    const btn = form.querySelector('.btn-add-to-cart');
    const label = form.querySelector('[data-add-text]');
    const original = label?.textContent;

    btn?.setAttribute('disabled', 'disabled');
    if (label) label.textContent = 'Adding…';

    try {
      await addToCart(variantId, 1);
      await refreshCartDrawer();
      openCart();
    } catch (err) {
      showToast(err.message || 'Could not add to cart');
    } finally {
      btn?.removeAttribute('disabled');
      if (label && original) label.textContent = original;
    }
  });
}

function readVariantJson(form) {
  const node = form.querySelector('[data-variant-json]');
  if (!node) return [];
  try {
    return JSON.parse(node.textContent);
  } catch {
    return [];
  }
}

// Matches on the option values actually chosen, so a product with
// one option (Size) or several (Size + Colour) both work.
function matchVariant(variants, selected, form) {
  const count = form.querySelectorAll('.size-selector-wrapper').length || 1;

  return variants.find((v) =>
    Array.from({ length: count }).every((_, i) => {
      const want = selected[String(i)];
      return want == null || v.options[i] === want;
    })
  );
}

function applyVariant(variant, select) {
  if (!variant || !select) return;

  select.value = variant.id;
  select.dispatchEvent(new Event('change', { bubbles: true }));

  const priceEl = document.querySelector('[data-price]');
  const compareEl = document.querySelector('[data-compare-price]');
  const badgeEl = document.querySelector('.price-badge');

  if (priceEl) priceEl.textContent = variant.price;

  if (compareEl) {
    compareEl.textContent = variant.compare || '';
    compareEl.style.display = variant.compare ? '' : 'none';
  }
  if (badgeEl) {
    badgeEl.textContent = variant.saving ? `SAVE ${variant.saving}` : '';
    badgeEl.style.display = variant.saving ? '' : 'none';
  }

  const btn = document.querySelector('.btn-add-to-cart');
  const label = document.querySelector('[data-add-text]');
  if (btn) btn.disabled = !variant.available;
  if (label) label.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
}

function initGallery() {
  const mainImg = document.querySelector('.pdp-main-img img');

  document.querySelectorAll('.pdp-thumb').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      document.querySelectorAll('.pdp-thumb').forEach((t) => t.classList.remove('active'));
      thumb.classList.add('active');
      if (mainImg) mainImg.src = thumb.dataset.image;
    });
  });
}

/* Save for later — localStorage, keyed by product handle */

const SAVED_KEY = 'rarelane_saved';

function getSaved() {
  try {
    return JSON.parse(localStorage.getItem(SAVED_KEY)) || [];
  } catch {
    return [];
  }
}

function initSaveButton() {
  const btn = document.querySelector('.btn-save');
  const handle = btn?.dataset.productHandle;
  if (!btn || !handle) return;

  const sync = () => {
    const saved = getSaved().includes(handle);
    btn.classList.toggle('saved', saved);
    btn.setAttribute('aria-label', saved ? 'Remove from saved' : 'Save for later');
    btn.setAttribute('aria-pressed', String(saved));
  };

  sync();

  btn.addEventListener('click', () => {
    const saved = getSaved();
    const i = saved.indexOf(handle);
    if (i === -1) {
      saved.push(handle);
      showToast('Saved for later');
    } else {
      saved.splice(i, 1);
      showToast('Removed from saved');
    }
    localStorage.setItem(SAVED_KEY, JSON.stringify(saved));
    sync();
  });
}

/* ============================================
   Floating video widget
   ============================================ */

function initVideoWidget() {
  const widget = document.querySelector('.video-widget');
  if (!widget) return;

  const video = widget.querySelector('video');
  const toggleBtn = widget.querySelector('.video-toggle');
  const muteBtn = widget.querySelector('.video-mute');
  const closeBtn = widget.querySelector('.video-close');

  toggleBtn?.addEventListener('click', () => {
    if (widget.classList.contains('expanded')) {
      widget.classList.remove('expanded');
      video?.pause();
    } else {
      widget.classList.add('expanded');
      // play() rejects if autoplay is blocked — swallow it
      video?.play().catch(() => {});
    }
  });

  muteBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!video) return;
    video.muted = !video.muted;
    muteBtn.classList.toggle('unmuted', !video.muted);
    muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
  });

  closeBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    widget.classList.remove('expanded');
    video?.pause();
  });
}

/* ============================================
   Size guide modal
   ============================================ */

function initSizeGuideModal() {
  const modal = document.querySelector('[data-modal="size-guide"]');
  if (!modal) return;

  const open = (e) => {
    e.preventDefault();
    modal.classList.add('open');
    document.body.classList.add('no-scroll');
    modal.querySelector('[data-modal-close]')?.focus();
  };

  const close = () => {
    modal.classList.remove('open');
    document.body.classList.remove('no-scroll');
  };

  document.querySelectorAll('[data-size-guide-open]').forEach((t) => t.addEventListener('click', open));
  modal.querySelector('[data-modal-close]')?.addEventListener('click', close);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) close();
  });
}

/* ============================================
   Account — address forms
   ============================================ */

function initAddressForms() {
  // Shopify's country options carry their states in a data-provinces JSON array.
  document.querySelectorAll('[data-address-country]').forEach((country) => {
    const province = document.getElementById(country.dataset.provinceTarget);
    if (!province) return;
    const wrap = document.querySelector(`[data-province-wrap-for="${country.dataset.provinceTarget}"]`);

    const fill = () => {
      const opt = country.options[country.selectedIndex];
      let list = [];
      try {
        list = JSON.parse(opt.dataset.provinces || '[]');
      } catch (err) {
        list = [];
      }
      province.innerHTML = '';
      if (!list.length) {
        if (wrap) wrap.hidden = true;
        return;
      }
      if (wrap) wrap.hidden = false;
      list.forEach(([value, label]) => {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        province.appendChild(o);
      });
      if (province.dataset.default) province.value = province.dataset.default;
    };

    if (country.dataset.default) country.value = country.dataset.default;
    country.addEventListener('change', fill);
    fill();
  });

  document.querySelectorAll('[data-confirm-message]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      if (!window.confirm(btn.dataset.confirmMessage)) e.preventDefault();
    });
  });
}

/* ============================================
   Collection page — sort
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  initAddressForms();

  const sort = document.querySelector('[data-sort-by]');
  sort?.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });
});

window.showToast = showToast;
window.openCart = openCart;
window.closeCart = closeCart;

/* ============================================================
   ADD-ON BLOCK — mobile bottom bars
   Appended on purpose: nothing above this line is modified, so this
   can be pasted onto a live theme.js as-is. It registers its own
   DOMContentLoaded listener rather than joining the init chain above,
   and it reads the existing markup instead of changing how any of it
   works.
   ============================================================ */

(function () {
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(() => {
    /* ---- Report the hamburger's state to screen readers.
       initMobileMenu already toggles .open on the menu, so this only
       watches that class and mirrors it — no existing code changes. */
    const toggle = document.querySelector('.nav-toggle');
    const menu = document.querySelector('.mobile-menu');

    if (toggle && menu) {
      if (!menu.id) menu.id = 'mobile-menu';
      toggle.setAttribute('aria-controls', menu.id);

      const reportMenu = () => {
        toggle.setAttribute('aria-expanded', menu.classList.contains('open') ? 'true' : 'false');
      };

      reportMenu();
      new MutationObserver(reportMenu).observe(menu, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    const bar = document.querySelector('.mobile-bar, [data-pdp-bar]');
    if (!bar) return;

    /* ---- Publish the bar's rendered height so the page bottom and the
       floating video widget clear it. Measured rather than hardcoded:
       the bar grows with the merchant's font settings. Resolves to 0 on
       desktop, where the bars are display:none. */
    const measure = () => {
      document.body.style.setProperty('--mbar-h', `${bar.offsetHeight}px`);
    };

    measure();
    window.addEventListener('resize', measure);
    if (document.fonts?.ready) document.fonts.ready.then(measure);

    /* Everything below is the product-page buy bar */
    const form = document.getElementById('product-form');
    if (!form || !bar.matches('[data-pdp-bar]')) return;

    const pageAdd = form.querySelector('.btn-add-to-cart');
    const pagePrice = document.querySelector('[data-price]');
    const pageCompare = document.querySelector('[data-compare-price]');
    const pageLabel = document.querySelector('[data-add-text]');

    const barAdd = bar.querySelector('[data-bar-add]');
    const barPrice = bar.querySelector('[data-bar-price]');
    const barCompare = bar.querySelector('[data-bar-compare]');
    const barLabel = bar.querySelector('[data-bar-add-text]');
    const barSize = bar.querySelector('[data-bar-size]');
    const wrappers = form.querySelectorAll('.size-selector-wrapper');

    /* The bar owns no state. It copies whatever the in-page block is
       showing, which applyVariant() already keeps correct. */
    const mirror = () => {
      if (barPrice && pagePrice) barPrice.textContent = pagePrice.textContent;

      if (barCompare) {
        const show = pageCompare
          && pageCompare.style.display !== 'none'
          && pageCompare.textContent.trim() !== '';
        barCompare.textContent = show ? pageCompare.textContent : '';
        barCompare.style.display = show ? '' : 'none';
      }

      if (barLabel && pageLabel) barLabel.textContent = pageLabel.textContent;
      if (barAdd && pageAdd) barAdd.disabled = pageAdd.disabled;

      if (barSize) {
        const picked = Array.from(wrappers, (w) =>
          w.querySelector('.size-selector button.active')?.dataset.size
        );
        barSize.textContent = picked.length && picked.every(Boolean)
          ? picked.join(' / ')
          : 'Select size';
      }
    };

    mirror();

    /* One observer over the price block and the form, instead of hooking
       each control: it catches the price, the label, the disabled state
       and the active size chip whatever changed them. Scoped to these two
       so a review or rating app mutating the rest of the column doesn't
       keep waking it up. */
    const watcher = new MutationObserver(mirror);
    [document.querySelector('.pdp-price'), form].forEach((target) => {
      if (!target) return;
      watcher.observe(target, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'disabled', 'style'],
      });
    });

    barAdd?.addEventListener('click', () => {
      /* Same size gate the form applies, but scrolled into view first:
         the bar can be tapped while the selector is far off-screen. */
      let missing = null;
      wrappers.forEach((w) => {
        if (!w.querySelector('.size-selector button.active')) {
          w.querySelector('.size-error')?.classList.add('visible');
          if (!missing) missing = w;
        }
      });

      if (missing) {
        missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.showToast?.('Please select a size first');
        return;
      }

      pageAdd?.click();
    });

    bar.querySelector('[data-bar-size-jump]')?.addEventListener('click', () => {
      wrappers[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

       /* Reveal the bar only once the real Add to Cart has scrolled up past the
       top of the screen. "Not intersecting" on its own is not enough: on a
       phone the button starts below the fold, so the bar would be up from
       page load. bottom < 0 means it is above the viewport. */
    const anchor = form.querySelector('.pdp-ctas') || pageAdd;
    const past = (box) => box.bottom < 0;

    if (!anchor || !('IntersectionObserver' in window)) {
      const onScroll = () => {
        bar.classList.toggle('visible', !!anchor && past(anchor.getBoundingClientRect()));
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      return;
    }

    new IntersectionObserver(
      ([entry]) => bar.classList.toggle(
        'visible',
        !entry.isIntersecting && past(entry.boundingClientRect)
      )
    ).observe(anchor);
  });
})();

/* ============================================================
   ADD-ON BLOCK 3 — inline search panel
   The search icon is a plain link to the search page, so tapping it left
   the store. This intercepts the click and drops a panel over the header
   instead, with live results from Shopify's own suggest endpoint.

   The panel is built here rather than in header.liquid so this needs no
   markup change, and the form inside it is a real GET form pointed at the
   same URL the icon used — with JS off, or on Enter, the search page still
   works exactly as before.
   ============================================================ */

(function () {
  const link = document.querySelector('.nav-actions a[href*="/search"]');
  if (!link) return;

  const searchUrl = link.getAttribute('href').split('?')[0];
  let panel, scrim, input, results, timer, lastTerm;

  /* Titles come from the catalogue, so results are built as DOM nodes
     instead of an HTML string — no escaping to get wrong. */
  const render = (products, term) => {
    results.textContent = '';
    if (!products.length) {
      const p = document.createElement('p');
      p.className = 'search-empty';
      p.textContent = `No matches for “${term}”. Try a brand or a colour.`;
      results.appendChild(p);
      return;
    }
    products.forEach((product) => {
      const hit = document.createElement('a');
      hit.className = 'search-hit';
      hit.href = product.url;
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = '';
        img.loading = 'lazy';
        hit.appendChild(img);
      }
      const label = document.createElement('span');
      label.textContent = product.title;
      hit.appendChild(label);
      results.appendChild(hit);
    });
    const all = document.createElement('a');
    all.className = 'search-all';
    all.href = `${searchUrl}?q=${encodeURIComponent(term)}&type=product`;
    all.textContent = `See all results for “${term}”`;
    results.appendChild(all);
  };

  const lookup = async (term) => {
    if (term === lastTerm) return;
    lastTerm = term;
    try {
      const res = await fetch(
        `${searchUrl}/suggest.json?q=${encodeURIComponent(term)}` +
        '&resources[type]=product&resources[limit]=6'
      );
      const data = await res.json();
      if (input.value.trim() === term) {
        render(data.resources?.results?.products || [], term);
      }
    } catch {
      /* offline or blocked — the form still submits to the search page */
    }
  };

  const close = () => {
    panel.classList.remove('open');
    scrim.classList.remove('open');
    document.body.classList.remove('search-open');
    link.setAttribute('aria-expanded', 'false');
  };

  const build = () => {
    scrim = document.createElement('div');
    scrim.className = 'search-scrim';

    panel = document.createElement('div');
    panel.className = 'search-panel';
    panel.id = 'search-panel';

    const form = document.createElement('form');
    form.className = 'search-panel-form';
    form.action = searchUrl;
    form.method = 'get';
    form.role = 'search';
    form.innerHTML =
      '<input type="hidden" name="type" value="product">' +
      '<input type="search" name="q" autocomplete="off" placeholder="Search sneakers, colours, styles…" aria-label="Search products">' +
      '<button type="button" class="search-panel-close" aria-label="Close search">&times;</button>';

    results = document.createElement('div');
    results.className = 'search-panel-results';
    results.setAttribute('aria-live', 'polite');

    panel.append(form, results);
    document.body.append(scrim, panel);

    input = form.querySelector('input[type="search"]');
    link.setAttribute('aria-controls', panel.id);

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) {
        results.textContent = '';
        lastTerm = null;
        return;
      }
      timer = setTimeout(() => lookup(term), 250);
    });

    form.querySelector('.search-panel-close').addEventListener('click', close);
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panel.classList.contains('open')) close();
    });
  };

  link.addEventListener('click', (e) => {
    e.preventDefault();
    if (!panel) build();
    document.querySelector('.mobile-menu.open') && document.querySelector('.nav-toggle')?.click();
    panel.classList.add('open');
    scrim.classList.add('open');
    document.body.classList.add('search-open');
    link.setAttribute('aria-expanded', 'true');
    input.focus();
  });

  link.setAttribute('aria-expanded', 'false');
})();

/* ============================================================
   ROUND 3 — mobile-menu close button + infinite product scroll
   Self-contained IIFE with its own DOMContentLoaded listener, so
   it does not need to join the init chain at the top of the file.
   ============================================================ */
(function () {
  /* The drawer's ✕ reuses the hamburger's existing handler rather than
     duplicating its open/close state. */
  function initMenuClose() {
    const close = document.querySelector('.mobile-menu-close');
    const toggle = document.querySelector('.nav-toggle');
    if (!close || !toggle) return;
    close.addEventListener('click', () => toggle.click());
  }

  /* Collection and search pages: append the next page as the shopper
     reaches the bottom. Shopify's numbered pagination stays in the DOM
     and is restored if a fetch fails, so this degrades to real paging. */
  function initInfiniteScroll() {
    const pager = document.querySelector('.pagination');
    if (!pager || !('IntersectionObserver' in window)) return;

    const grid =
      pager.parentElement.querySelector('.product-grid') ||
      document.querySelector('.product-grid');
    if (!grid) return;

    // paginate.parts always includes the last page, even when truncated
    const numbers = Array.from(pager.querySelectorAll('.pagination-link'))
      .map((el) => parseInt(el.textContent.trim(), 10))
      .filter((n) => Number.isFinite(n));
    const lastPage = numbers.length ? Math.max(...numbers) : Infinity;

    let page = parseInt(new URL(location.href).searchParams.get('page') || '1', 10);
    if (!Number.isFinite(page) || page < 1) page = 1;

    const status = document.createElement('div');
    status.className = 'infinite-status';
    status.setAttribute('aria-live', 'polite');
    status.textContent = 'Loading more';
    pager.before(status);
    pager.style.display = 'none';

    let loading = false;
    let done = page >= lastPage;
    if (done) {
      status.dataset.done = '';
      status.textContent = 'That’s everything';
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadNext();
      },
      { rootMargin: '600px 0px' }
    );

    function finish(text) {
      done = true;
      observer.disconnect();
      status.dataset.done = '';
      status.textContent = text;
    }

    async function loadNext() {
      if (loading || done) return;
      loading = true;

      const url = new URL(location.href);
      url.searchParams.set('page', String(page + 1));

      try {
        const res = await fetch(url.href);
        if (!res.ok) throw new Error('HTTP ' + res.status);

        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const cards = doc.querySelectorAll('.product-grid > *');
        if (!cards.length) {
          finish('That’s everything');
          return;
        }

        page += 1;
        cards.forEach((card) => grid.appendChild(document.importNode(card, true)));

        if (page >= lastPage) {
          finish('That’s everything');
          return;
        }

        /* Re-observing re-fires the callback with the sentinel's current
           position — without it a short page never triggers a second load. */
        observer.unobserve(status);
        observer.observe(status);
      } catch (err) {
        // Hand the shopper back real page links instead of a dead end.
        observer.disconnect();
        done = true;
        status.remove();
        pager.style.display = '';
      } finally {
        loading = false;
      }
    }

    observer.observe(status);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initMenuClose();
    initInfiniteScroll();
  });
})();

